import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';

import { getAccessToken } from '@/api/auth';
import { useTopology } from '@/app/topology';
import { createVideoSignallingConnection } from '@ngsos/portal-core/video';
import type { VideoSignallingConnection } from '@ngsos/portal-core/video';
import type { LiveVideo } from '@/signalr';
import type { IncidentRealtime } from '@/features/incident-detail/hub/incidentRealtime';
import {
  consume as mediasoupConsume,
  consumerEndCall,
  createDevice,
  createRecvTransport,
  disposeMediasoup,
  play as mediasoupPlay,
  registerMediasoupVideoBoxListener,
  startStatsCollection,
  unregisterMediasoupVideoBoxListener,
} from '@/lib/video/mediasoupService';
import type { MediasoupVideoBoxListener } from '@/lib/video/mediasoupService';
import type { ConsumerParams } from '@/lib/video/types/consumerParams';
import type { TransportParams } from '@/lib/video/types/transportParams';
import { initHls, destroyHls } from '@/lib/video/playbackService';
import { getVideoOptions } from '@/lib/video/config';
import { reportUnhandledError } from '@/lib/errors/unhandledErrors';
import {
  consumeActiveProducer,
  delay,
  throwIfAborted,
} from './consumeActiveProducer';
import {
  CALLER_VIDEO_CONNECTION_STATE_NAMES,
  CAMERA_STATE_NAMES,
  INITIAL_VIDEO_CALL_STATE,
  RECORDING_STATE_NAMES,
  isStaleProducerStop,
  videoCallReducer,
} from './videoCallMachine';
import type { VideoCallEvent, VideoCallState } from './videoCallMachine';

export type UseVideoCallOptions = {
  liveVideo: LiveVideo;
  realtime: IncidentRealtime | null;
  onVideoExpandedChanged: (expanded: boolean) => void;
};

export type UseVideoCallResult = {
  state: VideoCallState;
  videoServerUrl: string | null;
  toggleRecordingAndPauseOrResumeStream: () => Promise<void>;
  switchUserCamera: () => Promise<void>;
  takeSnapshot: () => Promise<void>;
  endLiveVideo: () => Promise<void>;
};

type MediasoupReady = {
  promise: Promise<void>;
  resolve: () => void;
  isCompleted: boolean;
};

function createMediasoupReady(): MediasoupReady {
  let resolve!: () => void;
  const promise = new Promise<void>((promiseResolve) => {
    resolve = promiseResolve;
  });
  const ready: MediasoupReady = {
    promise,
    isCompleted: false,
    resolve: () => {
      ready.isCompleted = true;
      resolve();
    },
  };
  return ready;
}

export function useVideoCall({ liveVideo, realtime, onVideoExpandedChanged }: UseVideoCallOptions): UseVideoCallResult {
  const topology = useTopology();
  const videoId = liveVideo.id;

  const [state, setState] = useState<VideoCallState>(INITIAL_VIDEO_CALL_STATE);
  const stateRef = useRef(state);
  const dispatch = useCallback((event: VideoCallEvent) => {
    stateRef.current = videoCallReducer(stateRef.current, event);
    setState(stateRef.current);
  }, []);

  const [videoServerUrl, setVideoServerUrl] = useState<string | null>(null);

  const videoHubRef = useRef<VideoSignallingConnection | null>(null);
  const deviceRtpCapabilitiesRef = useRef<unknown>(null);
  const mediasoupReadyRef = useRef<MediasoupReady | null>(null);
  mediasoupReadyRef.current ??= createMediasoupReady();
  const mediasoupReady = mediasoupReadyRef.current;
  const consumeAbortRef = useRef<AbortController | null>(null);
  const hlsStartedRef = useRef(false);

  const onVideoExpandedChangedRef = useRef(onVideoExpandedChanged);
  useEffect(() => {
    onVideoExpandedChangedRef.current = onVideoExpandedChanged;
  });
  const realtimeRef = useRef(realtime);
  useEffect(() => {
    realtimeRef.current = realtime;
  });

  const sendClientEvent = useCallback((eventName: string, data?: string) => {
    const datetime = dayjs().format('DD.MM.YYYY HH:mm:ss.SSS');
    const payload = data !== undefined ? { eventName, datetime, data } : { eventName, datetime };

    void videoHubRef.current?.sendClientEvent(payload).catch((error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      console.log(`[SendClientEvent] Failed to send event ${eventName} - error: ${message}`);
    });

    return Promise.resolve();
  }, []);

  const runConsumeActiveProducer = useCallback(async () => {
    await consumeActiveProducer({
      waitForMediasoupReady: () => mediasoupReady.promise,
      createAttemptSignal: () => {
        consumeAbortRef.current?.abort();
        consumeAbortRef.current = new AbortController();
        return consumeAbortRef.current.signal;
      },
      getActiveProducerId: () => stateRef.current.activeProducerId,
      getConsumedProducerId: () => stateRef.current.consumedProducerId,
      consumeOnce: async (producerId, signal) => {
        const videoHub = videoHubRef.current;
        if (videoHub === null) {
          throw new Error('Video hub connection is not initialized');
        }

        const consumerParams = (await videoHub.consume(
          producerId,
          deviceRtpCapabilitiesRef.current,
        )) as ConsumerParams;
        throwIfAborted(signal);

        await mediasoupConsume(consumerParams);
        throwIfAborted(signal);

        await videoHub.resumeConsumer();
        throwIfAborted(signal);

        await mediasoupPlay(consumerParams);
        throwIfAborted(signal);

        startStatsCollection();
      },
      onConsumed: (producerId) => dispatch({ type: 'CONSUME_SUCCEEDED', producerId }),
      onAttemptFailed: () => dispatch({ type: 'CONSUME_ATTEMPT_FAILED' }),
      sendClientEvent,
      delay,
      log: (message) => console.log(message),
    });
  }, [dispatch, sendClientEvent, mediasoupReady]);

  const initMediasoup = useCallback(
    async (rtpCapabilities: unknown) => {
      await sendClientEvent('[InitMediasoup] START');
      console.log('[InitMediasoup] START');

      deviceRtpCapabilitiesRef.current = await createDevice(
        rtpCapabilities as Parameters<typeof createDevice>[0],
      );
      const transportParams = (await videoHubRef.current?.createWebRtcTransport()) as TransportParams;
      await createRecvTransport(transportParams);

      console.log('[InitMediasoup] END');
      await sendClientEvent('[InitMediasoup] END');
    },
    [sendClientEvent],
  );

  const joinVideoRoom = useCallback(async () => {
    console.log('[JoinVideoRoom] START');
    await sendClientEvent('[JoinVideoRoom] START');

    const videoHub = videoHubRef.current;
    if (videoHub === null) {
      throw new Error('Video hub connection is not initialized');
    }

    const joinResult = await videoHub.joinRoom();
    dispatch({ type: 'JOIN_ROOM_SUCCEEDED', activeProducerId: joinResult.activeProducerId });

    await initMediasoup(joinResult.routerRtpCapabilities);
    if (!mediasoupReady.isCompleted) {
      mediasoupReady.resolve();
    }

    await runConsumeActiveProducer();
    console.log('[JoinVideoRoom] END');
    await sendClientEvent('[JoinVideoRoom] END');
  }, [dispatch, initMediasoup, runConsumeActiveProducer, sendClientEvent, mediasoupReady]);

  const handleTransportConnectionFailed = useCallback(async () => {
    if (stateRef.current.transportRecoveryInProgress) {
      return;
    }

    dispatch({ type: 'TRANSPORT_RECOVERY_STARTED' });

    try {
      console.log('[OnTransportConnectionFailed] Recovery started');

      consumeAbortRef.current?.abort();

      await consumerEndCall();

      await joinVideoRoom();

      console.log('[OnTransportConnectionFailed] Recovery finished');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.log(`[OnTransportConnectionFailed] Recovery failed: ${message}`);
    } finally {
      dispatch({ type: 'TRANSPORT_RECOVERY_FINISHED' });
    }
  }, [dispatch, joinVideoRoom]);

  useEffect(() => {
    if (realtime === null) {
      return;
    }

    let disposed = false;
    const unsubscribers: (() => void)[] = [];

    const videoHub = createVideoSignallingConnection({
      videoServerUrl: getVideoOptions().VIDEO_SERVER_URL,
      videoId,
      accessTokenFactory: async () => {
        const accessToken = await getAccessToken();
        if (accessToken == null) {
          throw new Error('Cannot join the video signalling without an access token.');
        }
        return accessToken;
      },
    });
    videoHubRef.current = videoHub;

    const initialize = async () => {
      console.log('[OnInitializedAsync] - MediasoupVideoBox - START');

      onVideoExpandedChangedRef.current(true);

      await videoHub.connect();

      const [videoState, liveState] = await Promise.all([videoHub.getVideoState(), realtime.getLiveState()]);
      const webAppState = liveState.webAppState;
      if (disposed) {
        return;
      }
      dispatch({
        type: 'STATES_LOADED',
        callerConnectionState: videoState.callerVideoConnectionState,
        recordingState: videoState.recordingState,
        cameraState: videoState.cameraState,
        webAppState,
      });

      unsubscribers.push(
        videoHub.onReconnect(() => {
          void (async () => {
            await sendClientEvent('[Reconnected] Rejoining video room');
            console.log('[Reconnected] Rejoining video room');
            await handleTransportConnectionFailed();
          })();
        }),
      );

      unsubscribers.push(
        videoHub.on('newProducerAvailable', (producerId) => {
          void (async () => {
            await sendClientEvent(`[NewProducerAvailable] ${producerId}`);
            console.log(`[NewProducerAvailable] ${producerId}`);
            dispatch({ type: 'NEW_PRODUCER_AVAILABLE', producerId });

            await runConsumeActiveProducer();
          })();
        }),
      );

      unsubscribers.push(
        videoHub.on('producerStopped', (stoppedProducerId) => {
          void (async () => {
            await sendClientEvent(`[ProducerStopped] ${stoppedProducerId}`);
            console.log(`[ProducerStopped] ${stoppedProducerId}`);

            if (isStaleProducerStop(stateRef.current, stoppedProducerId)) {
              const consumedProducerId = stateRef.current.consumedProducerId;
              await sendClientEvent(
                `[ProducerStopped] Ignoring stale stop for ${stoppedProducerId}, currently consuming ${String(consumedProducerId)}`,
              );
              console.log(
                `[ProducerStopped] Ignoring stale stop for ${stoppedProducerId}, currently consuming ${String(consumedProducerId)}`,
              );
              return;
            }

            dispatch({ type: 'PRODUCER_STOPPED', stoppedProducerId });
            await sendClientEvent('[ProducerStopped] Disconnected');
          })();
        }),
      );

      unsubscribers.push(
        videoHub.on('callerVideoConnectionStateChanged', (newState) => {
          void (async () => {
            dispatch({ type: 'CALLER_VIDEO_CONNECTION_STATE_CHANGED', state: newState });
            await sendClientEvent(
              `[CallerVideoConnectionStateChanged] ${CALLER_VIDEO_CONNECTION_STATE_NAMES[newState]}`,
            );
            if (newState === 'Connected') {
              onVideoExpandedChangedRef.current(true);
            }
          })();
        }),
      );

      unsubscribers.push(
        videoHub.on('recordingStateChanged', (newState) => {
          void (async () => {
            dispatch({ type: 'RECORDING_STATE_CHANGED', state: newState });
            await sendClientEvent(`[RecordingStateChanged] ${RECORDING_STATE_NAMES[newState]}`);
          })();
        }),
      );

      unsubscribers.push(
        videoHub.on('cameraStateChanged', (_videoId, newState) => {
          void (async () => {
            dispatch({ type: 'CAMERA_STATE_CHANGED', state: newState });
            await sendClientEvent(`[CameraStateChanged] ${CAMERA_STATE_NAMES[newState]}`);
          })();
        }),
      );

      unsubscribers.push(
        realtime.hub.on('WebAppStateChanged', (eventIncidentId, newState) => {
          if (eventIncidentId === realtime.incidentId) {
            dispatch({ type: 'WEB_APP_STATE_CHANGED', state: newState });
          }
        }),
      );

      console.log('[OnInitializedAsync] - MediasoupVideoBox - END');

      const listener: MediasoupVideoBoxListener = {
        connectWebRtcTransport: async (dtlsParameters) => {
          await videoHub.connectWebRtcTransport(dtlsParameters);
        },
        sendConsumerStats: async (stats) => {
          try {
            await videoHub.sendConsumerStats(stats);
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.log(`[SendConsumerStats] Failed to send stats: ${message}`);
          }
        },
        sendClientEvent,
        onTransportConnectionFailed: handleTransportConnectionFailed,
      };
      registerMediasoupVideoBoxListener(listener);

      setVideoServerUrl(getVideoOptions().VIDEO_SERVER_URL);

      try {
        await joinVideoRoom();
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        await sendClientEvent(`[OnAfterRenderAsync] JoinVideoRoom failed: ${message}`);
        console.log(`[OnAfterRenderAsync] JoinVideoRoom failed: ${message}`);
      }
    };

    void initialize().catch((error: unknown) => {
      console.error('Joining the video room failed.', error);
      reportUnhandledError(error);
    });

    return () => {
      disposed = true;
      consumeAbortRef.current?.abort();

      if (hlsStartedRef.current) {
        destroyHls();
      }

      disposeMediasoup();

      unsubscribers.forEach((unsubscribe) => unsubscribe());
      unregisterMediasoupVideoBoxListener();

      videoHubRef.current = null;
      videoHub.disconnect();
    };
  }, [
    videoId,
    realtime,
    topology.emsServerUrl,
    dispatch,
    sendClientEvent,
    runConsumeActiveProducer,
    joinVideoRoom,
    handleTransportConnectionFailed,
  ]);

  const toggleRecordingAndPauseOrResumeStream = useCallback(async () => {
    if (stateRef.current.showRecording) {
      await videoHubRef.current?.resumeConsumer();
      dispatch({ type: 'RESUME_LIVE_STREAM' });
    } else {
      dispatch({ type: 'REWIND_TO_RECORDING' });

      await videoHubRef.current?.pauseConsumer();

      hlsStartedRef.current = true;
      initHls(videoId);
    }
  }, [dispatch, videoId]);

  const switchUserCamera = useCallback(async () => {
    await realtimeRef.current?.switchCamera();
  }, []);

  const takeSnapshot = useCallback(async () => {
    await realtimeRef.current?.takeCallerCameraSnapshot();
  }, []);

  const endLiveVideo = useCallback(async () => {
    await consumerEndCall();
    onVideoExpandedChangedRef.current(false);
    await realtimeRef.current?.endLiveVideo();
  }, []);

  return {
    state,
    videoServerUrl,
    toggleRecordingAndPauseOrResumeStream,
    switchUserCamera,
    takeSnapshot,
    endLiveVideo,
  };
}
